import React, { useState, useEffect, FunctionComponent, useRef, RefObject, useMemo } from 'react'
import { useFetch, usePut, useDelete, usePost, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'
import { useResizeObserver } from 'usehooks-ts'

// Components
import CardLoading from '../../../../components/component-items/loading-popover'
import GlobalStyles from '../../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { OrderListFiltered } from '../batch/order-list'
import Directive from './wave-directive'
import styled from 'styled-components'
import { removeAtIndex, insertAtIndex, arrayMove, SortableItem } from '../../../../components/component-items/sortable'
import { DndContext, DragOverlay, TouchSensor, MouseSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { Tooltip } from './styles'
import { PermissionGate } from '../../../../components/rbac'
import { LargeButton } from '../../../../components/component-items/buttons'
import GenericModal from '../../../../components/component-items/modal'
import { Pages } from '../../../../components/component-items/pager'
import Select from 'react-select'

// Props
import { FiltersProps, SortProps } from './index'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faPlayCircle } from '@fortawesome/pro-duotone-svg-icons'

const DataTable = styled(GlobalStyles.DataTable)`
  tr:last-child {
    border-bottom: none;
  }
`

const normalizeOrderListRow = (order: any) => ({
  id: order.id,
  remoteId: order.remote_id ?? order.remoteId,
  company: order.company_name ?? order.company?.name ?? order.company,
  address: order.address,
  shipping: order.shipping,
  created: order.created,
  items: order.items || [],
  status: order.status,
  statusId: order.status_id ?? order.statusId,
})

const ProfileCard = styled.div`
  overflow: visible;
  margin-bottom: 1.25em;
  border: 1px solid #ebedf2;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(82, 63, 105, 0.08);

  .profile-header {
    display: flex;
    flex-wrap: wrap;
    gap: 1em;
    align-items: center;
    justify-content: space-between;
    padding: 1em;
    background: #f6f9ff;
    border-bottom: 1px solid #ebedf2;
    border-radius: 8px 8px 0 0;
  }

  .profile-title {
    h4 {
      margin: 0;
      color: #495057;
      font-size: 1.05rem;
    }

    p {
      margin: 0.35em 0 0;
      color: #74788d;
      font-size: 0.9rem;
    }
  }

  .profile-actions {
    display: flex;
    gap: 0.5em;
    align-items: center;
    justify-content: flex-end;
  }

  .empty-directives {
    padding: 1.25em;
    color: #74788d;
    text-align: center;
  }

  .profile-admin {
    display: flex;
    justify-content: flex-end;
    padding: 0.75em 1em;
    background: #fbfcfe;
    border-top: 1px solid #ebedf2;
    border-radius: 0 0 8px 8px;
  }

  .danger-link {
    border: 0;
    background: transparent;
    color: #74788d;
    font-size: 0.85rem;
    text-decoration: underline;

    &:hover {
      color: #f46a6a;
    }
  }
`

const ProfileToolbar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75em;
  align-items: center;
  justify-content: flex-end;

  .filter-field {
    flex: 0 1 360px;
    min-width: 280px;
    width: 360px;
  }

  button {
    flex: 0 0 auto;
  }
`

const WaveProfileContainer = styled(GlobalStyles.DataTableContainer)`
  min-height: 0;
`

const LoadingProfilePanel = styled.div`
  min-height: 320px;
  border: 1px solid #ebedf2;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(82, 63, 105, 0.08);
  position: relative;
`

export type WaveDirectivesProps = {
  id: number
  profile: string
  name: string
  last_run: string
  order_counts: { received: number; backordered: number; total: number }
  company_names: any[]
  index: number
  settings: any
  sort: SortProps[]
  filters: FiltersProps
  dragOverlay?: any
  style?: any
}

export type ProfileScheduleProps = {
  enabled: boolean
  cadence: string
  next_run: string
  timezone: string
}

export type ProfilesProps = {
  id: string
  index: number
  companies: any[]
  wave_directives: WaveDirectivesProps[]
  schedule?: ProfileScheduleProps
  uncovered_orders?: number | null
  uncovered_order_summary?: { label: string; value: number; count: number }[]
  uncovered_order_results?: any[]
}

type DroppableProps = { id: string; items: any[] }
const Droppable: FunctionComponent<DroppableProps> = ({ id, items }) => {
  const { setNodeRef } = useDroppable({ id })

  return (
    <SortableContext id={id} items={items} strategy={rectSortingStrategy}>
      <tbody className="droppable" ref={setNodeRef} style={{ padding: 0, margin: 0, minHeight: 60 }}>
        {items.map((item: WaveDirectivesProps) => (
          <SortableItem key={item.id} id={item.id}>
            <Directive {...item} />
          </SortableItem>
        ))}
      </tbody>
    </SortableContext>
  )
}

export const WaveCoverageModal = ({
  open,
  onOpenChange,
  selectedCompanyIds = [],
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCompanyIds?: string[]
}) => {
  const [coveragePage, setCoveragePage] = useState(1)
  const coverageParams = new URLSearchParams({ page: String(coveragePage) })
  if (selectedCompanyIds.length) coverageParams.set('company_ids', selectedCompanyIds.join(','))
  const coverageUrl = open ? `/api/inventory/wave-profile/coverage/?${coverageParams.toString()}` : ''
  const { response: coverageResponse, loaded: coverageLoaded, error: coverageError }: any = useFetch(coverageUrl, {})
  const uncoveredOrders = coverageLoaded ? (coverageResponse?.uncovered_orders ?? 0) : null
  const uncoveredOrderResults = coverageLoaded
    ? (coverageResponse?.uncovered_order_results || []).map(normalizeOrderListRow)
    : []
  const coverageTotalPages = coverageResponse?.total_pages || 1

  useEffect(() => {
    setCoveragePage(1)
  }, [selectedCompanyIds.join(',')])

  return (
    <GenericModal
      heading="Orders Not Covered"
      show={open}
      size="xl"
      onHide={() => onOpenChange(false)}
      buttons={<GlobalStyles.Button onClick={() => onOpenChange(false)}>Close</GlobalStyles.Button>}
    >
      <div style={{ padding: '0 1em 1em' }}>
        <StatusBoxStyled className="purple" style={{ margin: '0 0 1em' }}>
          These orders are ready for this warehouse and customer group, but no wave directive currently includes them.
          This is a wave configuration coverage signal, not an inventory shortage signal.
        </StatusBoxStyled>
        {coverageError ? (
          <CardLoading error={coverageError} />
        ) : !coverageLoaded ? (
          <CardLoading />
        ) : uncoveredOrderResults.length ? (
          <>
            <div style={{ color: '#74788d', marginBottom: '0.75em' }}>
              Showing {uncoveredOrderResults.length} of {uncoveredOrders || uncoveredOrderResults.length} orders not
              covered by wave configuration.
            </div>
            <OrderListFiltered orders={uncoveredOrderResults} />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1em' }}>
              <Pages page={coveragePage} setPage={setCoveragePage} total={coverageTotalPages} show={5} />
            </div>
          </>
        ) : (
          <div className="empty-directives">No uncovered orders found for the selected customers.</div>
        )}
      </div>
    </GenericModal>
  )
}

export const WaveProfile = ({
  title = 'Wave Profiles',
  embedded = false,
  toolbarActions,
}: {
  title?: string
  embedded?: boolean
  toolbarActions?: React.ReactNode
}) => {
  const {
    state: { csrf },
  } = globalState()

  const url = '/api/inventory/wave-profile/'
  const { response, loaded, error }: any = useFetch(url, {})
  const [profiles, setProfiles] = useState<any[]>([])

  useEffect(() => {
    if (loaded && Array.isArray(response)) {
      const sortedProfiles = response.map((profile: any) => {
        return { ...profile, wave_directives: profile.wave_directives.sort((a: any, b: any) => a.index - b.index) }
      })
      sortedProfiles.sort((a: any, b: any) => a.index - b.index)
      setProfiles(sortedProfiles)
    }
  }, [loaded, response])

  const [activeId, setActiveId] = useState(null)
  const ref = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<HTMLDivElement>({ ref: ref as RefObject<HTMLDivElement> })
  const width = size?.width ?? 950
  const [moved, setMoved] = useState<boolean>(false)
  const [runningProfiles, setRunningProfiles] = useState<string[]>([])
  const [companyFilters, setCompanyFilters] = useState<any[]>([])
  const companyFilterOptions = useMemo(
    () =>
      profiles
        .flatMap((profile) => profile.companies || [])
        .reduce((options: any[], company: any) => {
          if (!options.some((option) => option.value === company.value)) {
            options.push(company)
          }
          return options
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label)),
    [profiles]
  )
  const selectedCompanyIds = useMemo(() => companyFilters.map(({ value }) => value), [companyFilters])
  const displayProfiles = useMemo(
    () =>
      selectedCompanyIds.length
        ? profiles.filter((profile) =>
            profile.companies?.some((company: any) => selectedCompanyIds.includes(company.value))
          )
        : profiles,
    [profiles, selectedCompanyIds]
  )

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 1 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 2 } })
  )

  const handleDragStart = ({ active }: { active: any }) => setActiveId(active.id)

  const handleDragCancel = () => setActiveId(null)

  const handleDragOver = ({ active, over }: { active: any; over: any }) => {
    const overId = over?.id

    if (!overId) {
      return
    }

    const activeContainer = active.data.current.sortable.containerId
    const overContainer = over.data.current?.sortable.containerId || over.id

    if (activeContainer !== overContainer) {
      setProfiles((profiles: any) => {
        const activeIndex = active.data.current.sortable.index
        const groupIndex = profiles.findIndex(({ id }: any) => over.id === id)
        const overIndex =
          groupIndex !== -1 ? profiles[groupIndex]['wave_directives'].length + 1 : over.data.current.sortable.index

        return moveBetweenContainers(profiles, activeContainer, activeIndex, overContainer, overIndex, active.id)
      })
      setMoved(true)
    }
  }

  const handleDragEnd = ({ active, over }: { active: any; over: any }) => {
    // This condition is necessary to avoid calling updates on the backend multiple times since
    // handleDragOver will move between containers and handleDragEnd won't be called if
    // active.id equal to over.id or over is null
    if ((!over || active.id === over.id) && moved) {
      setProfiles((profiles) => {
        return reIndexList(profiles)
      })
      setMoved(false)
    }
    if (!over) {
      setActiveId(null)
      return
    }
    if (active.id !== over.id) {
      const activeContainer = active.data.current.sortable.containerId
      const overContainer = over.data.current?.sortable.containerId || over.id
      const activeIndex = active.data.current.sortable.index
      const groupIndex = profiles.findIndex(({ id }) => over.id === id)
      const overIndex =
        groupIndex !== -1 ? profiles[groupIndex]['wave_directives'].length + 1 : over.data.current.sortable.index
      setProfiles((profiles) => {
        const newItems = [...profiles]
        const activeContainerIndex = newItems.findIndex(({ id }) => id === activeContainer)
        const activeItem = newItems[activeContainerIndex]
        if (activeContainer === overContainer) {
          newItems.splice(activeContainerIndex, 1, {
            ...activeItem,
            wave_directives: arrayMove(activeItem['wave_directives'], activeIndex, overIndex),
          })
        }
        return reIndexList(newItems)
      })
    }
    setActiveId(null)
  }

  const moveBetweenContainers = (
    items: any[],
    activeContainer: any,
    activeIndex: number,
    overContainer: any,
    overIndex: number,
    itemId: string
  ) => {
    const newItems = [...items]
    const activeContainerIndex = newItems.findIndex(({ id }) => id === activeContainer)
    const overContainerIndex = newItems.findIndex(({ id }) => id === overContainer)
    const item = newItems[activeContainerIndex]['wave_directives'].find(({ id }: { id: string }) => id === itemId)
    item['settings']['companies'] = newItems[overContainerIndex]['companies']
    item['order_count'] = '(Reload)'
    newItems.splice(activeContainerIndex, 1, {
      ...newItems[activeContainerIndex],
      wave_directives: removeAtIndex(newItems[activeContainerIndex]['wave_directives'], activeIndex),
    })
    newItems.splice(overContainerIndex, 1, {
      ...newItems[overContainerIndex],
      wave_directives: insertAtIndex(newItems[overContainerIndex]['wave_directives'], overIndex, item),
    })
    return newItems
  }

  const reIndexList = (items: any[]) => {
    const newItems = items.map((profile) => {
      return {
        ...profile,
        wave_directives: profile.wave_directives.map((wave_directive: any, index: number) => {
          return { ...wave_directive, index: index + 1 }
        }),
      }
    })
    if (!loaded || error) {
      return newItems
    }
    usePut(
      `/api/inventory/wave-profile/index_directives/`,
      { profiles: newItems },
      csrf,
      false,
      true,
      false,
      false
    ).then((response) => {
      if (response && !response.error) {
        notify({ type: 'success', message: 'Successfully rearranged Wave Profile order' })
      }
    })
    return newItems
  }

  const runWave = async (id: string) => {
    if (confirm('Are you sure you want to run this group of directives?')) {
      setRunningProfiles((profiles) => profiles.concat(id))
      try {
        const response = await usePost(`/api/inventory/wave-profile/${id}/run_directives/`, {}, csrf, false)
        if (!response) {
          return
        }
        if (response.error) {
          notify({ type: 'warning', message: response?.error })
        } else {
          notify({ type: 'success', message: 'Batched Successfully!' })
        }
      } finally {
        setRunningProfiles((profiles) => profiles.filter((profileId) => profileId !== id))
      }
    }
  }

  const deleteWaveProfile = (id: string) => {
    useDelete(`/api/inventory/wave-profile/${id}/`, csrf)
  }

  const getProfileSummary = (waveDirectives: WaveDirectivesProps[]) => {
    const lastRun = waveDirectives
      .map(({ last_run }) => last_run)
      .filter(Boolean)
      .sort()
      .pop()

    return { lastRun }
  }

  const content = (
    <>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>{title}</h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <ProfileToolbar>
            <Select
              className="filter-field"
              isClearable
              isMulti
              options={companyFilterOptions}
              placeholder="Filter by customer..."
              value={companyFilters}
              onChange={(selected: any) => {
                setCompanyFilters(selected ? [...selected] : [])
              }}
            />
            {toolbarActions}
          </ProfileToolbar>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <div style={{ padding: '1em 1em 2em' }}>
        {!loaded && !error ? (
          <LoadingProfilePanel>
            <CardLoading />
          </LoadingProfilePanel>
        ) : (
          <WaveProfileContainer ref={ref}>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragCancel={handleDragCancel}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              {displayProfiles.map((profile) => {
                const { id: profileID, companies, wave_directives } = profile
                const { lastRun } = getProfileSummary(wave_directives)
                const companyNames = companies.map(({ label }: { label: string }) => label).join(', ')
                const nextScheduled =
                  profile.schedule?.enabled && profile.schedule.next_run
                    ? new Date(profile.schedule.next_run).toLocaleString()
                    : 'NA'
                return (
                  <ProfileCard key={profileID}>
                    <div className="profile-header">
                      <div className="profile-title">
                        <h4>{companyNames}</h4>
                        <p>
                          {wave_directives.length} directives · Last run{' '}
                          {lastRun ? new Date(lastRun).toLocaleString() : 'Never'} · Next scheduled: {nextScheduled}
                        </p>
                      </div>
                      <div className="profile-actions dropdown__container" data-no-dnd="true">
                        <Tooltip className="subheader__button button" data-title="Run all directives in this profile">
                          <LargeButton
                            onClick={() => runWave(profileID)}
                            disabled={!wave_directives.length || runningProfiles.includes(profileID)}
                            aria-label="Run all directives in this profile"
                          >
                            <FontAwesomeIcon icon={faPlayCircle} />
                          </LargeButton>
                        </Tooltip>
                      </div>
                    </div>
                    {wave_directives.length ? (
                      <div>
                        <DataTable style={{ tableLayout: 'fixed' }}>
                          <thead>
                            <tr>
                              <th style={{ width: '20px' }} />
                              <th style={{ width: '12%' }}>
                                <span className="text center">Sequence</span>
                              </th>
                              <th>
                                <span className="text center">Directive</span>
                              </th>
                              <th style={{ width: '18%' }}>
                                <span className="text center">Last Run</span>
                              </th>
                              <th style={{ width: '12%' }}>
                                <span className="text center">Received</span>
                              </th>
                              <th style={{ width: '12%' }}>
                                <span className="text center">Backordered</span>
                              </th>
                              <th style={{ width: '14%' }}>
                                <span className="text center">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <Droppable id={profileID} items={wave_directives} />
                        </DataTable>
                      </div>
                    ) : (
                      <div className="empty-directives">No directives in this wave profile.</div>
                    )}
                    <PermissionGate segment="outbound" minLevel="admin">
                      <div className="profile-admin" data-no-dnd="true">
                        <button
                          className="danger-link"
                          onClick={() => deleteWaveProfile(profileID)}
                          aria-label="Delete Profile"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} /> Delete Profile
                        </button>
                      </div>
                    </PermissionGate>
                  </ProfileCard>
                )
              })}
              {loaded && !displayProfiles.length ? (
                <div className="empty-directives">
                  {companyFilters.length
                    ? 'No wave profiles match the selected customers.'
                    : 'No saved wave profiles yet.'}
                </div>
              ) : null}
              <DragOverlay>
                {activeId
                  ? profiles.map(({ wave_directives }) =>
                      wave_directives.map((directive: any) =>
                        directive.id === activeId ? (
                          <Directive key={directive.id} dragOverlay {...directive} style={{ width: width / 7 }} />
                        ) : null
                      )
                    )
                  : null}
              </DragOverlay>
            </DndContext>
          </WaveProfileContainer>
        )}
      </div>
      {error ? <CardLoading error={error} /> : null}
    </>
  )

  return embedded ? content : <GlobalStyles.FullPageCard>{content}</GlobalStyles.FullPageCard>
}
